import * as Sentry from '@sentry/browser';
import { isAssetRescueActive } from './assetRescueState';

const PROBED_SESSION_KEY = 'medsender:cdn-reachability-probed';
const DEFAULT_TIMEOUT_MS = 5000;
const FAILURE_OUTCOMES = ['error', 'timeout'];

const getSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

// True when the browser can actually run a probe (target URL, fetch, and
// AbortController all present). Shared by runProbe (the network primitive) and
// reportCdnReachability (which bails before marking the session).
const probeSupported = ({ probeUrl, fetchImpl } = {}) => {
  try {
    const fetchFn = fetchImpl || window.fetch;
    return Boolean(probeUrl)
      && typeof fetchFn === 'function'
      && typeof window.AbortController === 'function';
  } catch {
    return false;
  }
};

const metaContent = (name) => {
  try {
    const meta = typeof document !== 'undefined'
      ? document.querySelector(`meta[name="${name}"]`)
      : null;
    return meta?.getAttribute('content') || '';
  } catch {
    return '';
  }
};

export const runProbe = async ({
  probeUrl,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  fetchImpl,
} = {}) => {
  try {
    if (!probeSupported({ probeUrl, fetchImpl })) {
      return 'error';
    }
    const fetchFn = fetchImpl || window.fetch;

    const controller = new window.AbortController();
    let timedOut = false;
    let timeoutId;

    const timeout = new Promise((resolve) => {
      timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
        resolve('timeout');
      }, timeoutMs);
    });

    const probe = Promise.resolve()
      .then(() => fetchFn(probeUrl, {
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      }))
      .then(() => 'reachable')
      .catch((error) => ((timedOut || error?.name === 'AbortError') ? 'timeout' : 'error'));

    return await Promise.race([probe, timeout]).finally(() => {
      if (timeoutId) window.clearTimeout(timeoutId);
    });
  } catch {
    return 'error';
  }
};

// Module-level guard: dedups within a single page load even when sessionStorage
// is unavailable (private mode / storage disabled), where the persisted marker
// below is inert. sessionStorage still extends the guard across reloads within
// the tab when it works. Set synchronously in markProbed before any await so a
// concurrent caller (login racing validateAuth during boot) bails regardless of
// storage support.
let probedThisLoad = false;

export const alreadyProbedThisSession = () => {
  if (probedThisLoad) return true;
  try {
    return getSessionStorage()?.getItem(PROBED_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

export const markProbed = () => {
  probedThisLoad = true;
  try {
    getSessionStorage()?.setItem(PROBED_SESSION_KEY, 'true');
  } catch {
    // Private browsing / storage-disabled sessions: the in-memory flag still
    // dedups within this page load.
  }
};

export const reportCdnReachability = async ({
  probeUrl,
  controlProbeUrl,
  deploymentType,
  organizationId,
  timeoutMs,
  fetchImpl,
} = {}) => {
  try {
    if (alreadyProbedThisSession() || !probeSupported({ probeUrl, fetchImpl })) {
      return;
    }

    // Mark before awaiting the probe: a blocked CDN attempt takes the full
    // timeout to resolve, and a login quickly followed by validateAuth/
    // getLoginInfo during boot would otherwise both see the session as unprobed
    // and emit duplicate timeout events. Marking synchronously here makes the
    // second concurrent caller bail.
    markProbed();

    const outcome = await runProbe({ probeUrl, timeoutMs, fetchImpl });

    if (!FAILURE_OUTCOMES.includes(outcome)) {
      return;
    }

    // CDN unreachable. Probe a non-GCP control origin (e.g. the legacy
    // CloudFront host) to classify the failure: control reachable ⇒ the
    // GCP/CDN path specifically is being dropped (the 34.120/16 black-hole
    // hypothesis behind specs/cdn-unreachable-gcs-fallback.md); control also
    // unreachable ⇒ a broader, provider-agnostic connectivity loss the GCS
    // fallback would not solve. Runs only on this (rare, already-slow) failure
    // path, and only when a control URL is configured.
    let controlOutcome = 'unconfigured';
    if (controlProbeUrl) {
      controlOutcome = await runProbe({ probeUrl: controlProbeUrl, timeoutMs, fetchImpl });
    }

    try {
      Sentry.captureMessage(`CDN unreachable for ${deploymentType} org`, {
        level: 'error',
        tags: {
          cdn_reachable: false,
          deployment_type: deploymentType,
          organization_id: organizationId,
          reason: outcome,
          // Distinguishes "CDN unreachable but the asset rescue watchdog kicked
          // in" (user is working, slowly) from "unreachable and blank" (watchdog
          // dark or rescue failed). Set by assetRescueWatchdog.inline.js.
          asset_rescue_active: isAssetRescueActive(),
          // reachable ⇒ CDN/GCP-specific drop; timeout/error ⇒ broader outage;
          // unconfigured ⇒ no control origin set (default).
          control_outcome: controlOutcome,
        },
      });
    } catch {
      // Browser Sentry may be blocked; the probe must never affect auth.
    }
  } catch {
    // The probe is best-effort and must never affect the app.
  }
};

// Public entry for the auth flow. Given the server-authoritative active org id
// (routing_org_id), read the probe config from the server-rendered metas and run
// the probe. Skips when there is no org to attribute the event to (the
// validateAuth no-refresh fast path supplies none) — an org-less event can't be
// grouped in the at-risk-org rollup, and skipping leaves the session unmarked so
// a later server-validated load can still probe.
export const reportCdnReachabilityAfterAuth = (organizationId) => {
  try {
    if (!organizationId) return undefined;
    // Returned (not awaited by callers) so the auth flow stays fire-and-forget
    // while tests can await completion.
    return reportCdnReachability({
      probeUrl: metaContent('cdn-probe-url'),
      // Optional non-GCP control origin; absent meta ⇒ control probe skipped.
      controlProbeUrl: metaContent('cdn-control-probe-url'),
      deploymentType: metaContent('m8-deployment-type') || 'stable',
      organizationId,
    });
  } catch {
    // Reachability reporting must never affect authentication.
    return undefined;
  }
};
